import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { App, Button, Dropdown } from "antd";
import type { AxiosError } from "axios";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import {
  CrossIcon,
  DeleteIcon,
  EditIcon,
  MenuIcon,
  SearchIcon,
} from "src/assets/svgs";
import {
  CustomButton,
  CustomSelect,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import { cn } from "sipli-fleet-core/utils/cn";
import {
  useMutationDeleteUser,
  useMutationResendAccessEmail,
  useMutationUpdateUserStatus,
} from "src/queries/users.queries";
import type { User } from "src/services/userService";
import { USER_STATUS_CONFIG, userDataIndexToColumnId } from "../constant";
import EpochDate from "../../../../sipli-fleet-core/components/EpochDate/EpochDate";

const SearchIconComponent = SearchIcon as unknown as ComponentType<{
  className?: string;
}>;
const CrossIconComponent = CrossIcon as unknown as ComponentType<{
  className?: string;
}>;
const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;
const EditIconComponent = EditIcon as unknown as ComponentType<{
  className?: string;
}>;
const MenuIconComponent = MenuIcon as unknown as ComponentType<{
  className?: string;
}>;
const EpochDateComponent = EpochDate as unknown as ComponentType<{
  date?: string | number | null;
  appendFormat?: string;
}>;

interface UsersPagination {
  page: number;
  size: number;
  total?: number;
}

interface UsersSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

interface UsersTableProps {
  data: User[];
  isLoading?: boolean;
  pagination: UsersPagination;
  setPagination: Dispatch<SetStateAction<{ page: number; size: number }>>;
  sorting: UsersSorting;
  setSorting: Dispatch<SetStateAction<UsersSorting>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  selectedClientId?: string | number;
  setSelectedClientId: Dispatch<SetStateAction<string | number | undefined>>;
  clientOptions: Array<{ label: string; value: string | number }>;
  isClientLookupLoading?: boolean;
  refetchList: () => Promise<unknown>;
  onEditUser: (record: User) => void;
  hideFilters?: boolean;
  showCustomerColumn?: boolean;
}

const getCustomerName = (record: User) => record.client?.companyName || record.client?.name || "--";

const getUserId = (record: User) => record.id ?? record.userId;

const isCustomerUser = (record: User) => record.userRole?.name === "Customer User";

const getApiErrorMessage = (error: unknown) => {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  return axiosError.response?.data?.message || axiosError.response?.data?.error;
};

const USER_STATUS_TRANSLATION_KEYS: Record<string, string> = {
  active: "active",
  blocked: "blocked",
  not_subscribed: "not_subscribed",
};

const normalizeUserState = (state?: string) => state?.trim().toLowerCase() ?? "";

const getResolvedUserStatus = (record: User): "active" | "blocked" | "not_subscribed" => {
  const normalizedState = normalizeUserState(record.state);

  if (normalizedState === "active") {
    return "active";
  }

  if (normalizedState === "blocked") {
    return "blocked";
  }

  if (normalizedState === "not_subscribed") {
    return "not_subscribed";
  }

  return record.active ? "active" : "blocked";
};

const UserStateBadge = ({ record }: { record: User }) => {
  const { t } = useTranslation();

  const resolvedStatus = getResolvedUserStatus(record);
  const statusColor = USER_STATUS_CONFIG[resolvedStatus];
  const translationKey = USER_STATUS_TRANSLATION_KEYS[resolvedStatus];
  const status = translationKey ? t(translationKey) : "--";

  if (record.userRole?.name !== "Customer User") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-black">
        {resolvedStatus === "active" ? (
          <CustomTag
            className="w-fit flex items-center"
            style={{
              color: "rgb(26, 153, 86)",
              backgroundColor: "rgb(210, 242, 225)",
            }}
          >
            {t("active")}
          </CustomTag>
        ) : (
          <CustomTag
            className="w-fit flex items-center"
            style={{
              color: "#CA334D",
              backgroundColor: "#FFEDF0",
            }}
          >
            {t("blocked")}
          </CustomTag>
        )}
      </span>
    );
  }

  return (
    <CustomTag
      className={cn(
        "w-fit flex items-center",
        {
          "": resolvedStatus === "not_subscribed",
        }
      )}
      style={{
        color: statusColor?.color,
        backgroundColor: statusColor?.backgroundColor,
      }}
    >
      {status}
    </CustomTag>
  );
};

const UserActionMenu = ({
  record,
  isStatusLoading,
  isResendLoading,
  onResendAccessEmail,
  onStatusChange,
}: {
  record: User;
  isStatusLoading: boolean;
  isResendLoading: boolean;
  onResendAccessEmail: (record: User) => Promise<void>;
  onStatusChange: (record: User) => Promise<void>;
}) => {
  const { t } = useTranslation();

  const resolvedStatus = getResolvedUserStatus(record);

  const handleResendAccessEmail = async (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    await onResendAccessEmail(record);
  };

  const handleStatusChange = async (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    await onStatusChange(record);
  };

  return (
    <div className="min-w-[180px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]">
      {isCustomerUser(record) ? (
        <Button
          type="text"
          loading={isResendLoading}
          disabled={isResendLoading || !record.username}
          className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!outline-none disabled:!cursor-not-allowed disabled:!text-black disabled:!opacity-50"
          onClick={handleResendAccessEmail}
        >
          {t("resend_access_email")}
        </Button>
      ) : null}
      <Button
        type="text"
        loading={isStatusLoading}
        className="!flex !items-center !justify-start !cursor-pointer !h-[36px] !w-full !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!outline-none"
        onClick={handleStatusChange}
      >
        {resolvedStatus === "active" ? t("block_user") : t("active_user")}
      </Button>
    </div>
  );
};

const UsersTable = ({
  data,
  isLoading = false,
  pagination,
  setPagination,
  sorting,
  setSorting,
  search,
  setSearch,
  selectedClientId,
  setSelectedClientId,
  clientOptions,
  isClientLookupLoading = false,
  refetchList,
  onEditUser,
  hideFilters = false,
  showCustomerColumn = true,
}: UsersTableProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [updatingUserId, setUpdatingUserId] = useState<User["id"] | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<User["id"] | null>(null);
  const [openActionUserId, setOpenActionUserId] = useState<User["id"] | null>(null);
  const { mutateAsync: updateStatus } = useMutationUpdateUserStatus();
  const { mutateAsync: deleteUser, isPending: isDeleteLoading } = useMutationDeleteUser();
  const { mutateAsync: resendAccessEmail, isPending: isResendEmailLoading } =
    useMutationResendAccessEmail();

  const handleResendAccessEmail = useCallback(
    async (record: User) => {
      if (isResendEmailLoading || !record.username) return;

      try {
        await resendAccessEmail({
          username: record.username,
          platform: "FDM",
        });
        setOpenActionUserId(null);
        message.success(t("email_sent_successfully"));
        await refetchList();
      } catch (error) {
        message.error(getApiErrorMessage(error) || t("something_went_wrong"));
      }
    },
    [isResendEmailLoading, message, refetchList, resendAccessEmail, t]
  );

  const handleStatusChange = useCallback(
    async (record: User) => {
      const userId = getUserId(record);
      if (!userId) return;

      setUpdatingUserId(userId);

      try {
        const resolvedStatus = getResolvedUserStatus(record);
        const nextState = resolvedStatus === "active" ? "BLOCKED" : "ACTIVE";
        await updateStatus({
          id: userId,
          active: nextState === "ACTIVE",
          isCustomerUser: isCustomerUser(record),
          params: {
            state: nextState,
            ...(record.client?.id && {
              customerId: record.client.id,
            }),
          },
        });
        setOpenActionUserId(null);
        message.success(t("user_status_updated_successfully"));
        await refetchList();
      } catch {
        message.error(t("failed_to_update_user_status"));
      } finally {
        setUpdatingUserId(null);
      }
    },
    [message, refetchList, t, updateStatus]
  );

  const handleTableChange: TableProps<User>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;
    const sortBy = dataIndex ? userDataIndexToColumnId[String(dataIndex)] : undefined;

    if (!normalizedSorter?.order || !sortBy) {
      setSorting({
        sortBy: "ID",
        sortDir: "DESC",
      });
    } else {
      setSorting({
        sortBy,
        sortDir: normalizedSorter.order === "ascend" ? "ASC" : "DESC",
      });
    }

    setPagination((previous) => ({
      ...previous,
      page: 0,
    }));
  };

  const handleDeleteConfirm = async () => {
    if (deleteUserId === null) return;

    try {
      await deleteUser(deleteUserId);
      message.success(t("user_deleted_successfully"));
      setDeleteUserId(null);
    } catch {
      message.error(t("failed_to_delete_user"));
    }
  };

  const columns: TableProps<User>["columns"] = useMemo(() => {
    const baseColumns = [
      {
        title: t("username"),
        dataIndex: "username",
        key: "username",
        sorter: true,
        render: (value: User["username"]) => (
          <CustomTypography.CustomText className="!font-[600] !text-[14px]">
            {value || "--"}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("name"),
        dataIndex: "name",
        key: "name",
        sorter: true,
        render: (value: User["name"]) => (
          <CustomTypography.CustomText className="!font-[600] !text-[14px]">
            {value || "--"}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("surname"),
        dataIndex: "surname",
        key: "surname",
        sorter: true,
        render: (value: User["surname"]) => (
          <CustomTypography.CustomText className="!font-[600] !text-[14px]">
            {value || "--"}
          </CustomTypography.CustomText>
        ),
      },
      {
        title: t("role"),
        dataIndex: "role",
        key: "role",
        sorter: true,
        render: (_: unknown, record: User) => record.userRole?.name || "--",
      },
      {
        title: t("customer"),
        dataIndex: "customer",
        key: "customer",
        render: (_: unknown, record: User) => getCustomerName(record),
      },
      {
        title: t("date_created"),
        dataIndex: "createDate",
        key: "createDate",
        sorter: true,
        render: (value: User["createDate"]) => (
          <EpochDateComponent date={value ?? null} appendFormat="HH:mm" />
        ),
      },
      {
        title: t("status"),
        dataIndex: "active",
        key: "active",
        sorter: true,
        width: 170,
        render: (_: unknown, record: User) => (
          <UserStateBadge record={record} />
        ),
      },
      {
        title: t("actions"),
        key: "actions",
        render: (_: unknown, record: User) => {
          const userId = getUserId(record);
          const isCurrentStatusLoading = updatingUserId === userId;

          return (
            <div className="flex items-center gap-2 users-actions">
              <CustomTooltip title={t("edit")}>
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("edit_user")}
                  icon={<EditIconComponent className="w-[16px] h-[16px]" />}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    onEditUser(record);
                  }}
                />
              </CustomTooltip>
              <CustomTooltip title={t("delete")}>
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("delete_user")}
                  icon={<DeleteIconComponent className="w-[15px] h-[16px]" />}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    const userId = getUserId(record);
                    if (userId !== undefined && userId !== null) {
                      setDeleteUserId(userId);
                    }
                  }}
                />
              </CustomTooltip>
              <Dropdown
                trigger={["click"]}
                open={openActionUserId === getUserId(record)}
                onOpenChange={(open) =>
                  setOpenActionUserId(open ? (getUserId(record) ?? null) : null)
                }
                dropdownRender={() => (
                  <UserActionMenu
                    record={record}
                    isStatusLoading={isCurrentStatusLoading}
                    isResendLoading={isResendEmailLoading}
                    onResendAccessEmail={handleResendAccessEmail}
                    onStatusChange={handleStatusChange}
                  />
                )}
                placement="bottomRight"
                overlayClassName="user-action-dropdown"
              >
                <CustomTooltip title={t("more_action")}>
                  <CustomButton
                    type="icon"
                    buttonSize="small"
                    aria-label={t("more_actions")}
                    icon={<MenuIconComponent className="w-[3px] h-[16px]" />}
                    className="ml-2"
                    onClick={(event) => event?.stopPropagation?.()}
                  />
                </CustomTooltip>
              </Dropdown>
            </div>
          );
        },
      },
    ];

    return showCustomerColumn
      ? baseColumns
      : baseColumns.filter((column) => column.key !== "customer");
  }, [
    handleResendAccessEmail,
    handleStatusChange,
    isResendEmailLoading,
    onEditUser,
    openActionUserId,
    showCustomerColumn,
    t,
    updatingUserId,
  ]);

  return (
    <>
      {!hideFilters && (
        <div className="flex items-center gap-3 px-[18px] -mt-2 mb-5.5">
          <CustomSharedInput
            allowClear={{
              clearIcon: (
                <CrossIconComponent className="mr-0.5 users-search-clear-icon w-[8.5px] h-[8.5px]" />
              ),
            }}
            prefix={<SearchIconComponent className="users-search-icon w-[14px] h-[14px]" />}
            placeholder={t("search")}
            value={search}
            className="global-filter-search-class !rounded-[4px]"
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((previous) => ({
                ...previous,
                page: 0,
              }));
            }}
          />
          <CustomSelect
            allowClear
            className="users-client-select"
            placeholder={t("select_client")}
            value={selectedClientId}
            loading={isClientLookupLoading}
            options={clientOptions}
            onChange={(value) => {
              setSelectedClientId(value);
              setPagination((previous) => ({
                ...previous,
                page: 0,
              }));
            }}
          />
        </div>
      )}

      <CustomTable<User>
        customClass="users-table"
        columns={columns}
        data={data}
        rowKey={(record) => getUserId(record) ?? record.username ?? ""}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={pagination.total ?? data.length}
        handlePagination={(page) =>
          setPagination((previous) => ({
            ...previous,
            page,
          }))
        }
        handlePageSizeChange={(size) =>
          setPagination({
            page: 0,
            size: Number(size),
          })
        }
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        sorting={sorting}
        onChange={handleTableChange}
        isLoading={isLoading}
        locale={{
          emptyText: t("no_data"),
        }}
      />

      <CustomSharedConfirmationModal
        open={deleteUserId !== null}
        onCancel={() => setDeleteUserId(null)}
        onConfirm={() => void handleDeleteConfirm()}
        title={t("confirm_delete_user")}
        description={t("the_deletion_operation_is_irreversible")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={isDeleteLoading}
        centered
      />
    </>
  );
};

export default UsersTable;
